import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatPercent } from "@/lib/format";
import { APP_NAME, LOGO_URL, PIE_COLORS } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { Sparkles, Loader2, RefreshCw, CalendarRange, ArrowUpRight, ArrowDownRight, FileDown } from "lucide-react";
import { toast } from "sonner";

const escHtml = (s) =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function mdToHtml(text) {
  const inline = (s) => escHtml(s).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  const lines = text.split("\n");
  let html = "";
  let inList = false;
  const closeList = () => { if (inList) { html += "</ul>"; inList = false; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { closeList(); continue; }
    if (line.startsWith("## ")) { closeList(); html += `<h2>${inline(line.slice(3))}</h2>`; }
    else if (line.startsWith("# ")) { closeList(); html += `<h2>${inline(line.slice(2))}</h2>`; }
    else if (/^[-*]\s+/.test(line)) { if (!inList) { html += "<ul>"; inList = true; } html += `<li>${inline(line.replace(/^[-*]\s+/, ""))}</li>`; }
    else { closeList(); html += `<p>${inline(line)}</p>`; }
  }
  closeList();
  return html;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const k = `${i}:${p.slice(0, 24)}`;
    return p.startsWith("**") && p.endsWith("**") ? (
      <strong key={k} className="text-foreground font-semibold">{p.slice(2, -2)}</strong>
    ) : (
      <span key={k}>{p}</span>
    );
  });
}

function Insight({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let bullets = [];
  const flush = (key) => {
    if (bullets.length) {
      out.push(
        <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-2 text-sm text-foreground/85">
          {bullets.map((b, i) => (<li key={`${key}-b-${i}-${b.slice(0, 20)}`}>{renderInline(b)}</li>))}
        </ul>
      );
      bullets = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) { flush(idx); return; }
    if (line.startsWith("## ")) {
      flush(idx);
      out.push(
        <h3 key={idx} className="font-heading text-base font-semibold text-foreground mt-5 mb-1.5 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-brand" />
          {line.replace(/^##\s*/, "")}
        </h3>
      );
    } else if (line.startsWith("# ")) {
      flush(idx);
      out.push(<h2 key={idx} className="font-heading text-lg font-semibold mt-4 mb-2">{line.replace(/^#\s*/, "")}</h2>);
    } else if (/^[-*]\s+/.test(line)) {
      bullets.push(line.replace(/^[-*]\s+/, ""));
    } else {
      flush(idx);
      out.push(<p key={idx} className="text-sm text-foreground/85 leading-relaxed my-1.5">{renderInline(line)}</p>);
    }
  });
  flush("end");
  return <div data-testid="ai-insight-content">{out}</div>;
}

function Metric({ label, value, accent, sub, testId }) {
  const color =
    accent === "danger" ? "text-danger" : accent === "success" ? "text-success" : accent === "brand" ? "text-brand" : "text-foreground";
  return (
    <Card className="p-5" data-testid={testId}>
      <p className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">{label}</p>
      <p className={`mt-2 font-mono text-2xl font-medium ${color}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

function CatTooltip({ active, payload, currency }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-popover/95 backdrop-blur border border-border rounded-md shadow-xl px-3 py-2 text-xs font-mono">
      <span style={{ color: p.payload.fill }}>{p.name}</span>: {formatMoney(p.value, currency, { compact: true })}
    </div>
  );
}

function CategoryDonut({ title, data, total, currency, accentColor, testId }) {
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-lg font-semibold">{title}</h3>
        <span className="font-mono text-sm font-medium" style={{ color: accentColor }}>
          {formatMoney(total, currency, { compact: true })}
        </span>
      </div>
      {data && data.length ? (
        <div className="flex items-center gap-4">
          <div className="h-[170px] w-[170px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="amount" nameKey="category" cx="50%" cy="50%" innerRadius={44} outerRadius={78} paddingAngle={2} stroke="#030305" strokeWidth={2}>
                  {data.map((e, i) => (<Cell key={e.category} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                </Pie>
                <Tooltip content={<CatTooltip currency={currency} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-1.5">
            {data.slice(0, 6).map((e, i) => (
              <div key={e.category} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-foreground/80">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  {e.category}
                </span>
                <span className="font-mono text-muted-foreground">{formatMoney(e.amount, currency, { compact: true })}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground py-10 text-center">No entries for this period.</p>
      )}
    </Card>
  );
}

export default function MIS() {
  const { currency, rates } = useCurrency();
  const [month, setMonth] = useState("all");
  const [m, setM] = useState(null);
  const [cats, setCats] = useState(null);
  const [insight, setInsight] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get(`/mis?currency=${currency}&month=${month}`).then(({ data }) => setM(data)).catch(() => {
      toast.error("Failed to load MIS metrics");
    });
  }, [currency, rates, month]);

  useEffect(() => {
    api.get(`/mis/categories?currency=${currency}&month=${month}`).then(({ data }) => setCats(data)).catch(() => {
      setCats(null);
    });
  }, [currency, rates, month]);

  useEffect(() => {
    setInsight(null);
    api.get(`/ai/insights/latest?currency=${currency}&month=${month}`).then(({ data }) => {
      if (data?.text) setInsight(data);
    }).catch(() => {
      // Silent — latest insight is optional; user can regenerate.
    });
  }, [currency, month]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/ai/insights`, { currency, month });
      setInsight({ ...data, text: data.insight ?? data.text });
      toast.success("AI insights generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate insights");
    } finally {
      setGenerating(false);
    }
  };

  const exportReport = () => {
    const fmt = (v) => formatMoney(v, currency);
    const metrics = [
      ["Annualized Revenue", fmt(m.arr)],
      ["MRR", fmt(m.mrr)],
      [`Revenue (${m.period_label})`, fmt(m.revenue_ytd)],
      [`Net Profit (${m.period_label})`, fmt(m.net_profit_ytd)],
      ["Gross Margin", `${m.gross_margin}%`],
      ["Net Margin", `${m.net_margin}%`],
      ["MoM Growth", `${m.growth_mom}%`],
      ["Annualized Trend", `${m.growth_yoy}%`],
      ["Cash Balance", fmt(m.cash_balance)],
      ["Monthly Burn", m.burn_rate ? fmt(m.burn_rate) : "—"],
      ["Runway", m.runway_months ? `${m.runway_months} months` : "Cash-flow positive"],
      ["Accounts Receivable", fmt(m.accounts_receivable)],
      ["Accounts Payable", fmt(m.accounts_payable)],
      ["Fixed Investments", fmt(m.total_investments)],
    ];
    if (m.fd_count > 0) {
      metrics.push(["Fixed Deposits (Portfolio Value)", fmt(m.total_fd_value || m.total_fd_principal)]);
      metrics.push(["FD Principal", fmt(m.total_fd_principal)]);
      metrics.push(["FD Interest Accrued", fmt((m.total_fd_value || 0) - (m.total_fd_principal || 0))]);
      metrics.push(["FD Count (Active / Total)", `${m.fd_active_count || 0} / ${m.fd_count}`]);
    }
    if (cats) {
      metrics.push(["Total Income (ledger)", fmt(cats.total_income)]);
      metrics.push(["Total Expenses (ledger)", fmt(cats.total_expense)]);
    }
    const rowsHtml = metrics.map(([k, v]) => `<tr><td>${escHtml(k)}</td><td class="num">${escHtml(v)}</td></tr>`).join("");
    const catHtml = cats
      ? `<h2>Income by Category</h2><table>${cats.income_by_category.map((e) => `<tr><td>${escHtml(e.category)}</td><td class="num">${escHtml(fmt(e.amount))}</td></tr>`).join("") || '<tr><td class="muted">No income</td><td></td></tr>'}</table>
         <h2>Expense by Category</h2><table>${cats.expense_by_category.map((e) => `<tr><td>${escHtml(e.category)}</td><td class="num">${escHtml(fmt(e.amount))}</td></tr>`).join("") || '<tr><td class="muted">No expenses</td><td></td></tr>'}</table>`
      : "";
    const insightHtml = insight?.text
      ? mdToHtml(insight.text)
      : '<p class="muted">No AI insight generated for this period yet. Click "Generate" on the MIS page to include the narrative.</p>';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${APP_NAME} — MIS ${m.period_label}</title>
<style>
*{box-sizing:border-box}body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0f172a;margin:0;padding:48px;background:#fff}
.head{display:flex;align-items:center;gap:14px;border-bottom:3px solid #0055FF;padding-bottom:18px}
.head img{height:44px;width:44px;object-fit:contain}
.brand{font-size:22px;font-weight:800;letter-spacing:-0.02em}.brand b{color:#0055FF}
.sub{color:#64748b;font-size:13px;margin:14px 0 28px}.sub strong{color:#0f172a}
h2{font-size:14px;text-transform:uppercase;letter-spacing:.12em;color:#0055FF;margin:30px 0 12px;border-bottom:1px solid #e2e8f0;padding-bottom:6px}
table{width:100%;border-collapse:collapse;margin-bottom:8px}
td{padding:9px 4px;border-bottom:1px solid #eef2f7;font-size:14px}
td.num{text-align:right;font-variant-numeric:tabular-nums;font-weight:600}
.insight p{font-size:13.5px;line-height:1.6;color:#1e293b;margin:8px 0}
.insight ul{margin:8px 0;padding-left:20px}.insight li{font-size:13.5px;line-height:1.55;margin:4px 0}
.insight strong{color:#0f172a}.muted{color:#94a3b8}
.foot{margin-top:36px;border-top:1px solid #e2e8f0;padding-top:12px;color:#94a3b8;font-size:11px}
@media print{body{padding:24px}}
</style></head><body>
<div class="head"><img src="${LOGO_URL}" alt="logo"/><div class="brand">Ethara<b>.AI</b></div></div>
<div class="sub">Management Information System &middot; Board Briefing<br/>Reporting period: <strong>${m.period_label}</strong> &middot; Currency: <strong>${currency}</strong> &middot; ${new Date().toLocaleDateString()}</div>
<h2>Key Metrics</h2>
<table>${rowsHtml}</table>
${catHtml}
<div class="insight"><h2>AI Investor Briefing</h2>${insightHtml}</div>
<div class="foot">Generated by ${APP_NAME} on ${new Date().toLocaleString()} · Figures derived from live financial data.</div>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "width=900,height=1000");
    if (!w) {
      URL.revokeObjectURL(url);
      toast.error("Please allow pop-ups to export the PDF");
      return;
    }
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch {
        // Print dialog may be blocked by popup rules — silent fallback.
      }
      URL.revokeObjectURL(url);
    }, 700);
  };

  if (!m) {
    return (
      <div className="py-32 flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-brand" />
      </div>
    );
  }

  const runway = m.runway_months ? `${m.runway_months} mo` : "Cash positive";
  const periodSub = m.period_label;

  return (
    <div className="space-y-7">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">MIS &amp; AI Investor Insights</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Board-level metrics & AI narrative · <span className="text-foreground font-medium">{periodSub}</span> · in {currency}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-muted-foreground" />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[200px]" data-testid="mis-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {m.available_months.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`mis-month-${opt.value}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportReport} data-testid="export-pdf-btn" className="gap-2">
            <FileDown className="h-4 w-4" />
            <span className="hidden sm:inline">Export PDF</span>
          </Button>
        </div>
      </div>

      {/* Investor metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Annualized Revenue" value={formatMoney(m.arr, currency, { compact: true })} accent="success" sub="MRR × 12 · SaaS metric" testId="mis-metric-annualized-revenue" />
        <Metric label="MRR" value={formatMoney(m.mrr, currency, { compact: true })} sub="Monthly recurring" />
        <Metric label="AR Outstanding" value={formatMoney(m.accounts_receivable, currency, { compact: true })} accent="brand" sub="Unpaid client invoices" testId="mis-metric-ar-outstanding" />
        <Metric label="AP Outstanding" value={formatMoney(m.accounts_payable, currency, { compact: true })} accent={m.accounts_payable > 0 ? "warning" : "success"} sub="Unpaid vendor invoices" testId="mis-metric-ap-outstanding" />
        <Metric label="MoM Growth" value={formatPercent(m.growth_mom)} accent={m.growth_mom >= 0 ? "success" : "danger"} sub="Revenue" />
        <Metric label="Annualized Trend" value={formatPercent(m.growth_yoy)} accent={m.growth_yoy >= 0 ? "success" : "danger"} sub="vs 12m ago" />
        <Metric label="Gross Margin" value={`${m.gross_margin}%`} sub="Revenue efficiency" />
        <Metric label="Net Margin" value={`${m.net_margin}%`} accent={m.net_margin >= 0 ? "success" : "danger"} sub="Bottom line" />
        <Metric label="Cash Balance" value={formatMoney(m.cash_balance, currency, { compact: true })} accent="brand" sub="Liquidity" />
        <Metric label="Monthly Burn" value={m.burn_rate ? formatMoney(m.burn_rate, currency, { compact: true }) : "—"} accent={m.burn_rate ? "danger" : "success"} sub={`Runway: ${runway}`} />
        {m.fd_count > 0 && (
          <>
            <Metric
              label="FD Portfolio"
              value={formatMoney(m.total_fd_value || m.total_fd_principal, currency, { compact: true })}
              accent="brand"
              sub={`${m.fd_active_count || 0} active · ${m.fd_count} total`}
              testId="mis-metric-fd"
            />
            <Metric
              label="FD Interest Accrued"
              value={formatMoney((m.total_fd_value || 0) - (m.total_fd_principal || 0), currency, { compact: true })}
              accent="success"
              sub={`Principal ${formatMoney(m.total_fd_principal, currency, { compact: true })}`}
              testId="mis-metric-fd-interest"
            />
          </>
        )}
      </div>

      {/* Income & Expense by Category (from ledgers) */}
      {cats && (
        <div>
          <h3 className="font-heading text-lg font-semibold mb-3">
            Income &amp; Expense by Category{" "}
            <span className="text-xs text-muted-foreground font-normal">· {periodSub} · from ledgers</span>
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <CategoryDonut testId="mis-income-categories" title="Income by Category" data={cats.income_by_category} total={cats.total_income} currency={currency} accentColor="#22D122" />
            <CategoryDonut testId="mis-expense-categories" title="Expense by Category" data={cats.expense_by_category} total={cats.total_expense} currency={currency} accentColor="#FF3333" />
          </div>
        </div>
      )}

      {/* AI panel + Monthly trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 overflow-hidden border border-brand2/25 shadow-glow-violet">
          <div className="relative px-6 py-5 border-b border-border bg-gradient-to-r from-brand2/15 via-brand2/[0.05] to-transparent">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-brand2/15 border border-brand2/40 flex items-center justify-center shadow-glow-violet">
                  <Sparkles className="h-5 w-5 text-brand2" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold">AI Investor Briefing</h3>
                  <p className="text-xs text-muted-foreground">Claude Sonnet 4.6 · {periodSub}</p>
                </div>
              </div>
              <Button onClick={generate} disabled={generating} data-testid="generate-insights-btn" className="gap-2 shrink-0">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : insight ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                {generating ? "Generating…" : insight ? "Regenerate" : "Generate"}
              </Button>
            </div>
          </div>
          <div className="p-6">
            {generating ? (
              <div className="py-10 flex flex-col items-center justify-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mb-3 text-brand" />
                <p className="text-sm">Analysing {periodSub} financials & drafting your MIS narrative…</p>
              </div>
            ) : insight ? (
              <div>
                <Insight text={insight.text} />
                <p className="text-[11px] text-muted-foreground mt-5 pt-3 border-t border-border">
                  Generated {new Date(insight.created_at || insight.generated_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <div className="py-10 flex flex-col items-center justify-center text-center text-muted-foreground">
                <Sparkles className="h-8 w-8 mb-3 text-brand/60" />
                <p className="text-sm max-w-md">
                  Generate an investor-grade narrative for <span className="text-foreground">{periodSub}</span> covering growth,
                  profitability, liquidity, runway and recommended actions — written from your numbers.
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold mb-1">Monthly Trend</h3>
          <p className="text-xs text-muted-foreground mb-3">Click a month to drill in</p>
          <div className="space-y-1" data-testid="mis-monthly-trend">
            <button
              onClick={() => setMonth("all")}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                month === "all" ? "bg-brand/10 border border-brand/30" : "hover:bg-muted border border-transparent"
              }`}
              data-testid="trend-row-all"
            >
              <span className="font-medium">Trailing 12 Months</span>
              <span className="font-mono text-xs text-muted-foreground">12m</span>
            </button>
            {[...m.trend].reverse().map((t) => {
              const up = t.growth >= 0;
              const active = month === t.period;
              return (
                <button
                  key={t.period}
                  onClick={() => setMonth(t.period)}
                  data-testid={`trend-row-${t.period}`}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
                    active ? "bg-brand/10 border border-brand/30" : "hover:bg-muted border border-transparent"
                  }`}
                >
                  <span className="text-sm font-medium">{t.month}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-xs text-muted-foreground">{formatMoney(t.revenue, currency, { compact: true })}</span>
                    <span className={`inline-flex items-center gap-0.5 text-xs font-medium w-14 justify-end ${up ? "text-success" : "text-danger"}`}>
                      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {Math.abs(t.growth)}%
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
